import { useState, useCallback } from 'react';
import styled from '@emotion/styled';
import { Textarea } from '../presentation/Textarea';
import { Chip, ChipList, ChipRemove } from './Chip';

type Props = {
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
};

export const StringArrayEditor = ({
  value,
  onChange,
  placeholder = 'Add item...',
  disabled = false,
}: Props) => {
  const [input, setInput] = useState('');

  const handleAdd = useCallback(() => {
    const trimmed = input.trim();
    if (trimmed && !value.includes(trimmed)) {
      onChange([...value, trimmed]);
      setInput('');
    }
  }, [input, value, onChange]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleAdd();
      }
    },
    [handleAdd]
  );

  const handleRemove = useCallback(
    (index: number) => {
      onChange(value.filter((_, i) => i !== index));
    },
    [value, onChange]
  );

  return (
    <Root>
      {!disabled && (
        <InputRow>
          <StyledTextarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            minRows={1}
            maxRows={1}
          />
          <AddButton onClick={handleAdd} type="button" disabled={!input.trim()}>
            + Add
          </AddButton>
        </InputRow>
      )}
      <ChipList>
        {value.map((item, index) => (
          <Chip key={`${item}-${index}`}>
            {item}
            {!disabled && <ChipRemove onClick={() => handleRemove(index)} />}
          </Chip>
        ))}
      </ChipList>
    </Root>
  );
};

StringArrayEditor.displayName = 'StringArrayEditor';

// ─── Styled ─────────────────────────────────────────────────────────────────

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const InputRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const StyledTextarea = styled(Textarea)`
  flex: 1;
`;

const AddButton = styled.button`
  font-size: 14px;
  font-family: var(--font-bold);
  color: var(--pri-02);
  background: none;
  border: none;
  cursor: pointer;
  white-space: nowrap;

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }

  &:hover:not(:disabled) {
    text-decoration: underline;
  }
`;
